import { Injectable } from '@angular/core';

import {
  IEntityConfig,
  ENTITY_TYPES,
  BackendApiType,
  IFilterQuery,
  IDocumentOperationOptions,
  IApiListResponse,
  SiteType,
  ISite,
  ObjectId,
  EPRODUCT_TYPES,
} from '@app/shared/types';

import { BaseService } from '../base';
import { Observable } from 'rxjs';
import { lazyInject } from '@app/containers/base/decorators';
import { XprtDataService } from '../xprt-data';
import { DmDataService } from '../dm-data';
import { rx } from '@app/shared/rx';
import { get, includes, isEmpty, map } from 'lodash';

const entityType: ENTITY_TYPES = ENTITY_TYPES.PRODUCT;
const config: IEntityConfig = {
  backendApiType: BackendApiType.DM,
  ENTITY_TYPE: entityType,
  isMasterListAvailable: true,
  apiEndpoint: entityType,
  uiRoute: entityType,
  customEntityRoute: {
    list: 'master',
  },
  buttons: {},
};

@Injectable()
export class ProductService extends BaseService {
  @lazyInject(XprtDataService) public xprtDataService: XprtDataService;
  @lazyInject(DmDataService) public dmDataService: DmDataService;

  public static config: IEntityConfig = config;
  public static ENTITY_TYPE: string = config.ENTITY_TYPE;

  public fetchListByCommissarySites(
    userQuery?: IFilterQuery,
    options: IDocumentOperationOptions = {},
  ): Observable<IApiListResponse> {
    return this.xprtDataService
      .getAllSitesByFilter({
        filter_by_assigned_sites: false,
        filter: `business_type eq in(${SiteType.VendingCommissary})`,
        select: ['id'],
      })
      .pipe(
        rx.switchMap((sites) => {
          return this.fetchCurrentListBySites(sites, userQuery, options);
        }),
        rx.take(1),
      );
  }

  public fetchCurrentListBySites(
    sites: (ISite | { id: string })[],
    userQuery?: IFilterQuery,
    options: IDocumentOperationOptions = {},
  ): Observable<IApiListResponse> {
    return this.dmDataService
      .getSiteGroupId(map(sites, 'id'))
      .pipe(
        rx.switchMap((siteGroupId) =>
          this.fetchCurrentList(userQuery, [], siteGroupId, options),
        ),
      );
  }

  public fetchCurrentList(
    query: IFilterQuery = {},
    siteIds: ObjectId[] = [],
    siteGroupId: ObjectId = null,
    options: IDocumentOperationOptions = {},
  ): Observable<any> {
    const defaultTypeFilter = `product_type eq in(${[
      EPRODUCT_TYPES.STANDARD,
      EPRODUCT_TYPES.RETAIL_ITEM,
      EPRODUCT_TYPES.MODIFIER,
      EPRODUCT_TYPES.ENTITLEMENT,
      EPRODUCT_TYPES.GIFT_CARD,
    ].join(',')})`;

    const queryFilter = get(query, 'filter', '');

    let filterWithDefault = queryFilter;

    if (!includes(queryFilter, 'product_type')) {
      filterWithDefault = isEmpty(queryFilter)
        ? defaultTypeFilter
        : [defaultTypeFilter, `(${queryFilter})`].join(' and ');
    }

    return super.fetchCurrentList(
      { ...query, filter: filterWithDefault },
      siteIds,
      siteGroupId,
      options,
    );
  }
}
